import { countFamiliesNeedingRerun } from "../../../../api/pipelineMoveBpa.service.js";
import { firmLabel, isMismatchWarning, targetLabel } from "./moveBpaLabels.js";
import type { MoveBpaRowResult, MoveBpaRowStatus } from "./useMoveBpaBatch.js";
import styles from "../PipelineBrowser.module.css";

export const STATUS_LABELS: Record<MoveBpaRowStatus, string> = {
  ok: "ok",
  partial: "partial (approval sync failed)",
  failed: "failed",
  unknown: "unknown (rerun to confirm)",
};

function Warnings({ warnings }: { warnings: string[] }) {
  if (warnings.length === 0) return null;
  return (
    <ul className={styles.problemList}>
      {warnings.map((warning, index) => (
        <li key={index} className={isMismatchWarning(warning) ? styles.mappingError : undefined}>
          {warning}
        </li>
      ))}
    </ul>
  );
}

type MoveBpaRowResultsProps = {
  results: MoveBpaRowResult[];
  dryRun: boolean;
};

export function MoveBpaRowResults({ results, dryRun }: MoveBpaRowResultsProps) {
  return (
    <div className={styles.tableContainer}>
      <table className={styles.crossRefTable} aria-label="Per-row results">
        <thead>
          <tr>
            <th>Line</th>
            <th>Firm</th>
            <th>Target</th>
            <th>Status</th>
            <th>Roots</th>
            <th>Children</th>
            <th>Already in place</th>
            {!dryRun && <th>Moved (approval / testimonials / items)</th>}
            <th>Approval failures</th>
            <th>Families needing rerun</th>
            <th>Warnings / error</th>
          </tr>
        </thead>
        <tbody>
          {results.map(({ row, status, report, error }) => (
            <tr key={row.line}>
              <td>{row.line}</td>
              <td>{firmLabel(row)}</td>
              <td>{targetLabel(row)}</td>
              <td className={status === "ok" ? undefined : styles.mappingError}>
                {STATUS_LABELS[status]}
              </td>
              <td>{report?.matched.roots ?? "-"}</td>
              <td>{report?.matched.children ?? "-"}</td>
              <td>{report?.matched.alreadyInPlace ?? "-"}</td>
              {!dryRun && (
                <td>
                  {report
                    ? `${report.moved.approvalRequests} / ${report.moved.testimonials} / ${report.moved.processingItems}`
                    : "-"}
                </td>
              )}
              <td>{report?.approvalSync.failed.length ?? "-"}</td>
              <td>{report ? countFamiliesNeedingRerun(report) : "-"}</td>
              <td>
                {error && <div className={styles.mappingError}>{error}</div>}
                {report?.pipelineApply.error && (
                  <div className={styles.mappingError}>Pipeline: {report.pipelineApply.error}</div>
                )}
                {report && <Warnings warnings={report.warnings} />}
                {!error && !report?.pipelineApply.error && report?.warnings.length === 0 && "-"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
